// src/components/contracts/ContractTemplates.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import PreviewPaper from '../prescriptions/PreviewPaper';
import {
  listContractTemplates,
  createContractTemplate,
  updateContractTemplate,
  deleteContractTemplate,
  getBranding,
} from '../../services/contractsApi';

const getId = (o) => o?.id || o?._id;

export default function ContractTemplates() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [branding, setBranding] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    template_name: '',
    category: '',          // ex.: "ortodontia", "implante", "cirurgia", "consentimento"
    title: '',
    introduction_text: '',
    conclusion_text: '',
    clauses: [],           // [{ clause_number, title, content, is_required, category }]
  });

  const pageRef = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [data, brand] = await Promise.all([
        listContractTemplates(),
        getBranding(),
      ]);
      setItems(Array.isArray(data) ? data : []);
      setBranding(brand || {});
    } catch (e) {
      console.error(e);
      toast.error('Falha ao carregar templates de contratos');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      template_name: '',
      category: '',
      title: '',
      introduction_text: '',
      conclusion_text: '',
      clauses: [],
    });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      template_name: t.template_name || t.title || '',
      category: t.category || '',
      title: t.title || '',
      introduction_text: t.introduction_text || '',
      conclusion_text: t.conclusion_text || '',
      clauses: Array.isArray(t.clauses) ? t.clauses : [],
    });
    setShowModal(true);
  };

  // CLONAR: abre como "novo"
  const openClone = (t) => {
    setEditing(null);
    setForm({
      template_name: `${t.template_name || t.title || 'Template'} (cópia)`,
      category: t.category || '',
      title: t.title || '',
      introduction_text: t.introduction_text || '',
      conclusion_text: t.conclusion_text || '',
      clauses: Array.isArray(t.clauses) ? JSON.parse(JSON.stringify(t.clauses)) : [],
    });
    setShowModal(true);
  };

  const save = async (e, { asNew = false } = {}) => {
    e?.preventDefault?.();
    if (!form.template_name?.trim()) {
      toast.error('Informe um nome para o template.');
      return;
    }
    try {
      setLoading(true);
      if (editing && !asNew) {
        if (editing.is_system) {
          toast.warn('Templates “Padrão do sistema” só podem ser usados como base.');
        } else {
          await updateContractTemplate(getId(editing), form);
          toast.success('Template atualizado!');
        }
      } else {
        await createContractTemplate({ ...form, is_system: false });
        toast.success('Template criado!');
      }
      setShowModal(false);
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Erro ao salvar template');
    } finally { setLoading(false); }
  };

  const removeItem = async (id, isSystem) => {
    if (isSystem) {
      toast.warn('Templates “Padrão do sistema” não podem ser excluídos.');
      return;
    }
    if (!window.confirm('Excluir este template?')) return;
    try {
      setLoading(true);
      await deleteContractTemplate(id);
      toast.success('Template excluído');
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('Falha ao excluir template');
    } finally { setLoading(false); }
  };

  // Helpers de UI para cláusulas
  const addClause = () => {
    setForm((f) => ({
      ...f,
      clauses: [...(f.clauses || []), { clause_number: (f.clauses?.length || 0) + 1, title: '', content: '', is_required: false, category: '' }],
    }));
  };
  const updateClause = (idx, key, val) => {
    setForm((f) => {
      const copy = [...(f.clauses || [])];
      copy[idx] = { ...(copy[idx] || {}), [key]: val };
      return { ...f, clauses: copy };
    });
  };
  const removeClause = (idx) => {
    setForm((f) => {
      const copy = [...(f.clauses || [])];
      copy.splice(idx, 1);
      return { ...f, clauses: copy.map((c, i) => ({ ...c, clause_number: i + 1 })) };
    });
  };

  // ----------- PREVIEW -----------
  const compiledBody = useMemo(() => {
    let compiled = '';
    if (form.introduction_text?.trim()) compiled += `${form.introduction_text.trim()}\n\n`;
    if (Array.isArray(form.clauses) && form.clauses.length > 0) {
      compiled += 'CLÁUSULAS:\n\n';
      form.clauses.forEach((c) => {
        const num = c.clause_number ? `${c.clause_number}. ` : '';
        const title = c.title || 'Cláusula';
        const content = c.content || '';
        compiled += `${num}${title}\n${content}\n\n`;
      });
    }
    if (form.conclusion_text?.trim()) compiled += `${form.conclusion_text.trim()}\n`;
    return compiled.trim();
  }, [form]);

  // Monta um DAS PARTES de exemplo (placeholders) só para visualização do TEMPLATE
  const buildDasPartesPreview = () => {
    const dentista = branding?.doctor_name || '[NOME_DENTISTA]';
    const esp = branding?.specialty ? `, ${branding.specialty}` : '';
    const cro = branding?.cro_number ? `, CRO ${branding.cro_number}` : ' [CRO]';
    const endClinica = branding?.address || '[ENDERECO_CLINICA]';

    return (
`DAS PARTES

CONTRATANTE: [NOME_PACIENTE], CPF [CPF_PACIENTE], residente e domiciliado(a) em [ENDERECO_PACIENTE].
CONTRATADO: ${dentista}${esp}${cro}, com consultório localizado em ${endClinica}.`
    );
  };

  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Templates de Contratos</h2>
        <div className="flex gap-2">
          {/* Sem botão “Carregar Templates Padrão” */}
          <button onClick={openNew} className="px-3 py-2 bg-purple-600 text-white rounded-md">
            Novo Template
          </button>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div>Carregando...</div>
      ) : (
        <div className="space-y-2">
          {items.length === 0 && <div className="text-sm text-gray-500">Nenhum template cadastrado.</div>}
          {items.map((t) => (
            <div key={getId(t)} className="border rounded-md p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium">{t.template_name || t.title || 'Template'}</div>
                  <div className="text-xs text-gray-500">
                    {t.category ? t.category : 'sem categoria'}
                    {t.is_system && <span className="ml-2 inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700">Padrão do sistema</span>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => openClone(t)} className="px-3 py-1 border rounded-md">Usar como base</button>
                  {!t.is_system && (
                    <>
                      <button onClick={() => openEdit(t)} className="px-3 py-1 border rounded-md">Editar</button>
                      <button onClick={() => removeItem(getId(t), false)} className="px-3 py-1 border rounded-md">Excluir</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal com Edição + Pré-visualização */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 p-4 sm:p-6 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-6xl rounded-lg p-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">{editing ? 'Editar Template' : 'Novo Template'}</h3>
              <button onClick={()=>setShowModal(false)} className="px-3 py-1 border rounded-md">Fechar</button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Formulário */}
              <form onSubmit={(e)=>save(e)} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-sm text-gray-700">Nome do template</span>
                    <input
                      className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.template_name}
                      onChange={(e)=>setForm({...form, template_name: e.target.value})}
                      placeholder="Ex.: Contrato de tratamento ortodôntico"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm text-gray-700">Categoria</span>
                    <input
                      className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.category}
                      onChange={(e)=>setForm({...form, category: e.target.value})}
                      placeholder="Ex.: ortodontia, implante, cirurgia, consentimento"
                    />
                  </label>

                  <label className="block md:col-span-2">
                    <span className="text-sm text-gray-700">Título (opcional)</span>
                    <input
                      className="mt-1 w-full border rounded-md px-3 py-2"
                      value={form.title}
                      onChange={(e)=>setForm({...form, title: e.target.value})}
                      placeholder="Título exibido no contrato"
                    />
                  </label>

                  <label className="block md:col-span-2">
                    <span className="text-sm text-gray-700">Introdução (opcional)</span>
                    <textarea
                      className="mt-1 w-full border rounded-md px-3 py-2 min-h-[120px]"
                      value={form.introduction_text}
                      onChange={(e)=>setForm({...form, introduction_text: e.target.value})}
                      placeholder="Texto introdutório do contrato..."
                    />
                  </label>
                </div>

                <div className="mt-3 border-t pt-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">Cláusulas</div>
                    <button type="button" onClick={addClause} className="px-3 py-1 border rounded-md">Adicionar cláusula</button>
                  </div>
                  <div className="mt-2 space-y-3">
                    {(form.clauses || []).map((c, idx) => (
                      <div key={idx} className="border rounded-md p-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label className="block">
                            <span className="text-sm text-gray-700">Número</span>
                            <input
                              type="number"
                              className="mt-1 w-full border rounded-md px-3 py-2"
                              value={c.clause_number ?? idx + 1}
                              onChange={(e)=>updateClause(idx, 'clause_number', parseInt(e.target.value, 10))}
                            />
                          </label>
                          <label className="block">
                            <span className="text-sm text-gray-700">Título</span>
                            <input
                              className="mt-1 w-full border rounded-md px-3 py-2"
                              value={c.title || ''}
                              onChange={(e)=>updateClause(idx, 'title', e.target.value)}
                            />
                          </label>
                          <label className="block md:col-span-2">
                            <span className="text-sm text-gray-700">Conteúdo</span>
                            <textarea
                              className="mt-1 w-full border rounded-md px-3 py-2 min-h-[100px]"
                              value={c.content || ''}
                              onChange={(e)=>updateClause(idx, 'content', e.target.value)}
                              placeholder="Use [[NB]] e [[/NB]] para trechos curtos que NÃO devem quebrar entre páginas"
                            />
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={!!c.is_required}
                              onChange={(e)=>updateClause(idx, 'is_required', e.target.checked)}
                            />
                            Cláusula obrigatória
                          </label>
                          <label className="block">
                            <span className="text-sm text-gray-700">Categoria (opcional)</span>
                            <input
                              className="mt-1 w-full border rounded-md px-3 py-2"
                              value={c.category || ''}
                              onChange={(e)=>updateClause(idx, 'category', e.target.value)}
                              placeholder="Ex.: ortodontia"
                            />
                          </label>
                        </div>

                        <div className="mt-2 flex justify-end">
                          <button type="button" onClick={()=>removeClause(idx)} className="px-3 py-1 border rounded-md">
                            Remover
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <label className="block mt-3">
                  <span className="text-sm text-gray-700">Conclusão (opcional)</span>
                  <textarea
                    className="mt-1 w-full border rounded-md px-3 py-2 min-h-[100px]"
                    value={form.conclusion_text}
                    onChange={(e)=>setForm({...form, conclusion_text: e.target.value})}
                    placeholder="Fechamento / assinaturas... (dica: use [[NB]]ASSINATURAS...[[/NB]] para manter junto)"
                  />
                </label>

                <div className="flex items-center justify-end gap-2 pt-3 border-t mt-3">
                  {editing && !editing?.is_system && (
                    <button
                      type="button"
                      onClick={(e)=>save(e, { asNew: true })}
                      className="px-3 py-2 border rounded-md"
                    >
                      Salvar como novo
                    </button>
                  )}
                  {editing?.is_system && (
                    <button
                      type="button"
                      onClick={(e)=>save(e, { asNew: true })}
                      className="px-3 py-2 border rounded-md"
                    >
                      Salvar cópia
                    </button>
                  )}
                  <button type="button" onClick={()=>setShowModal(false)} className="px-3 py-2 border rounded-md">
                    Cancelar
                  </button>
                  {!editing?.is_system && (
                    <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-md">
                      Salvar
                    </button>
                  )}
                  {editing?.is_system && (
                    <button type="button" disabled className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md cursor-not-allowed">
                      Padrão do sistema
                    </button>
                  )}
                </div>
              </form>

              {/* Pré-visualização A4 (lado direito) */}
              <div className="border rounded-md overflow-auto max-h-[70vh]">
                <PreviewPaper
                  captureRef={pageRef}
                  branding={branding || {}}
                  content={{
                    title: form.title || form.template_name || 'Contrato',
                    contract_text: compiledBody,
                    parties_block: buildDasPartesPreview(),
                    issue_date: new Date().toISOString(),
                    doctor_name: branding?.doctor_name,
                  }}
                  variant="contract"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}